import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { b2bGetAccount, b2bUpdateAccount, b2bDeleteAccount, b2bCreateContacts } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmt(n, prefix = '') {
  if (!n) return '—';
  if (n >= 1e9) return prefix + (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return prefix + (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return prefix + (n / 1e3).toFixed(0) + 'K';
  return prefix + n;
}

function Chips({ value, max = 12 }) {
  const [expanded, setExpanded] = useState(false);
  if (!value) return <span style={{ color: '#94a3b8' }}>—</span>;
  const all = value.split(',').map(s => s.trim()).filter(Boolean);
  const shown = expanded ? all : all.slice(0, max);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {shown.map((t, i) => (
        <span key={i} style={chip}>{t}</span>
      ))}
      {all.length > max && (
        <button onClick={() => setExpanded(e => !e)} style={moreBtn}>
          {expanded ? 'less' : `+${all.length - max} more`}
        </button>
      )}
    </div>
  );
}

const chip = { background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '3px 8px', fontSize: 11 };
const moreBtn = { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 4, color: BLUE, fontSize: 11, cursor: 'pointer', padding: '3px 8px' };

export default function B2BAccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', title: '', phone: '' });
  const [savingContact, setSavingContact] = useState(false);

  function load() {
    setLoading(true);
    b2bGetAccount(id).then(r => { setData(r); setForm(r.account); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, [id]);

  async function handleAddContact(e) {
    e.preventDefault(); setSavingContact(true);
    try {
      await b2bCreateContacts({ ...contactForm, account_id: parseInt(id) });
      setShowAddContact(false);
      setContactForm({ first_name: '', last_name: '', email: '', title: '', phone: '' });
      load();
    } catch (err) { alert(err.message); }
    setSavingContact(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await b2bUpdateAccount(id, { ...form, employee_count: form.employee_count ? parseInt(form.employee_count) : null });
      setEditing(false);
      load();
    } catch (err) { alert(err.message); }
    setSaving(false);
  }

  if (loading) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-accounts" /><main style={{ padding: 40, color: '#64748b' }}>Loading…</main></div>;
  if (!data) return <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}><AdminSidebar active="b2b-accounts" /><main style={{ padding: 40, color: '#ef4444' }}>Not found</main></div>;

  const acct = data.account;
  const location = [acct.city, acct.state, acct.country].filter(Boolean).join(', ');

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-accounts" />
      <main style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/b2b/accounts')}>← Accounts</button>

        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>{acct.company_name}</h1>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
              {acct.website && <a href={acct.website.startsWith('http') ? acct.website : `https://${acct.website}`} target="_blank" rel="noreferrer" style={{ color: BLUE, fontSize: 13 }}>{acct.website}</a>}
              {acct.linkedin_url && <a href={acct.linkedin_url} target="_blank" rel="noreferrer" style={{ color: BLUE, fontSize: 13 }}>LinkedIn ↗</a>}
              {acct.standard_industry && <span style={s.indBadge}>{acct.standard_industry}</span>}
              {acct.size_bucket && <span style={s.sizeBadge}>{acct.size_bucket} employees</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.editBtn} onClick={() => setEditing(e => !e)}>{editing ? 'Cancel' : 'Edit'}</button>
            <button style={s.deleteBtn} onClick={async () => { if (confirm('Delete account and all contacts?')) { await b2bDeleteAccount(id); navigate('/admin/b2b/accounts'); } }}>Delete</button>
          </div>
        </div>

        {editing ? (
          <div style={s.card}>
            <form onSubmit={handleSave} style={{ padding: 20, display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={s.label}>Company Name<input required style={s.inp} value={form.company_name || ''} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} /></label>
                <label style={s.label}>Website<input style={s.inp} value={form.website || ''} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} /></label>
                <label style={s.label}>Industry<input style={s.inp} value={form.industry || ''} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} /></label>
                <label style={s.label}>ERP System<input style={s.inp} value={form.erp_system || ''} onChange={e => setForm(f => ({ ...f, erp_system: e.target.value }))} /></label>
                <label style={s.label}>Employees<input style={s.inp} type="number" value={form.employee_count || ''} onChange={e => setForm(f => ({ ...f, employee_count: e.target.value }))} /></label>
                <label style={s.label}>LinkedIn<input style={s.inp} value={form.linkedin_url || ''} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} /></label>
                <label style={s.label}>City<input style={s.inp} value={form.city || ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} /></label>
                <label style={s.label}>State<input style={s.inp} value={form.state || ''} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></label>
                <label style={s.label}>Country<input style={s.inp} value={form.country || ''} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} /></label>
              </div>
              <label style={s.label}>Notes<textarea style={{ ...s.inp, height: 80, resize: 'vertical' }} value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" style={s.primaryBtn} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* Key metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                ['Employees',     acct.employee_count?.toLocaleString()],
                ['Revenue',       fmt(acct.annual_revenue, '$')],
                ['Total Funding', fmt(acct.total_funding, '$')],
                ['Founded',       acct.founded_year],
                ['Location',      location],
              ].map(([label, val]) => (
                <div key={label} style={s.statCard}>
                  <div style={s.statLabel}>{label}</div>
                  <div style={{ ...s.statVal, color: label === 'Revenue' || label === 'Total Funding' ? '#059669' : NAVY }}>{val || '—'}</div>
                </div>
              ))}
            </div>

            {/* Description */}
            {acct.short_description && (
              <div style={{ ...s.card, marginBottom: 20, padding: '16px 20px' }}>
                <div style={s.sectionLabel}>About</div>
                <div style={{ color: '#374151', fontSize: 13, lineHeight: 1.7 }}>{acct.short_description}</div>
              </div>
            )}

            {/* Intel grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ ...s.card, padding: '16px 20px' }}>
                <div style={s.sectionLabel}>Technologies</div>
                <Chips value={acct.technologies} max={15} />
              </div>
              <div style={{ ...s.card, padding: '16px 20px' }}>
                <div style={s.sectionLabel}>Keywords</div>
                <Chips value={acct.keywords} max={12} />
              </div>
            </div>

            {/* Codes + raw industry */}
            {(acct.sic_codes || acct.naics_codes || acct.industry) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  ['Raw Industry',  acct.industry],
                  ['SIC Codes',     acct.sic_codes],
                  ['NAICS Codes',   acct.naics_codes],
                ].map(([label, val]) => (
                  <div key={label} style={{ ...s.statCard }}>
                    <div style={s.statLabel}>{label}</div>
                    <div style={{ color: '#374151', fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>{val || '—'}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            {acct.notes && (
              <div style={{ ...s.card, marginBottom: 20, padding: '16px 20px' }}>
                <div style={s.sectionLabel}>Notes</div>
                <div style={{ color: '#374151', fontSize: 13, lineHeight: 1.6 }}>{acct.notes}</div>
              </div>
            )}
          </>
        )}

        {/* Contacts */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Contacts ({data.contacts.length})</span>
            <button style={s.primaryBtn} onClick={() => setShowAddContact(true)}>+ Add Contact</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Name', 'Title', 'Standard Title', 'Seniority', 'Dept', 'Email', 'Email Status', ''].map(h => <th key={h} style={s.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.contacts.length === 0 && <tr><td colSpan={8} style={s.empty}>No contacts</td></tr>}
                {data.contacts.map(c => (
                  <tr key={c.contact_id} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 500, color: NAVY, whiteSpace: 'nowrap' }}>
                      <Link to={`/admin/b2b/contacts/${c.contact_id}`} style={{ color: NAVY, textDecoration: 'none' }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</Link>
                    </td>
                    <td style={{ ...s.td, color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || '—'}</td>
                    <td style={s.td}>{c.standard_title ? <span style={s.stdBadge}>{c.standard_title}</span> : '—'}</td>
                    <td style={{ ...s.td, fontSize: 12, color: '#64748b' }}>{c.seniority || '—'}</td>
                    <td style={{ ...s.td, fontSize: 12, color: '#64748b' }}>{c.departments || '—'}</td>
                    <td style={{ ...s.td, color: BLUE, fontSize: 12 }}>{c.email || '—'}</td>
                    <td style={s.td}>
                      {c.email_status ? (
                        <span style={{ ...s.statusBadge, background: c.email_status === 'Verified' ? 'rgba(5,150,105,0.1)' : 'rgba(148,163,184,0.15)', color: c.email_status === 'Verified' ? '#059669' : '#64748b' }}>
                          {c.email_status}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={s.td}><Link to={`/admin/b2b/contacts/${c.contact_id}`} style={{ color: BLUE, fontSize: 12 }}>View →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showAddContact && (
          <div style={s.modal}>
            <div style={s.modalBox}>
              <h2 style={{ margin: '0 0 16px', color: NAVY, fontSize: 18 }}>Add Contact to {acct.company_name}</h2>
              <form onSubmit={handleAddContact} style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input required style={s.inp} placeholder="First Name *" value={contactForm.first_name} onChange={e => setContactForm(f => ({ ...f, first_name: e.target.value }))} />
                  <input required style={s.inp} placeholder="Last Name *" value={contactForm.last_name} onChange={e => setContactForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
                <input style={s.inp} placeholder="Title / Role" value={contactForm.title} onChange={e => setContactForm(f => ({ ...f, title: e.target.value }))} />
                <input type="email" style={s.inp} placeholder="Email" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} />
                <input style={s.inp} placeholder="Phone" value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button type="button" style={s.cancelBtn} onClick={() => setShowAddContact(false)}>Cancel</button>
                  <button type="submit" style={s.primaryBtn} disabled={savingContact}>{savingContact ? 'Saving…' : 'Add Contact'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:       { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  backBtn:    { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 8px', display: 'block' },
  header:     { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title:      { color: NAVY, fontSize: 24, fontWeight: 700, margin: 0 },
  indBadge:   { background: 'rgba(1,118,206,0.1)', color: BLUE, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
  sizeBadge:  { background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '3px 10px', fontSize: 12 },
  editBtn:    { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, cursor: 'pointer', fontSize: 13, padding: '7px 16px' },
  deleteBtn:  { background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: 8, color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '7px 16px' },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancelBtn:  { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: 20 },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  cardTitle:  { color: NAVY, fontSize: 14, fontWeight: 600 },
  sectionLabel: { color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 },
  statCard:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' },
  statLabel:  { color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 },
  statVal:    { color: NAVY, fontSize: 15, fontWeight: 600 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty:      { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  stdBadge:   { background: 'rgba(243,116,40,0.1)', color: ORANGE, borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600 },
  statusBadge:{ borderRadius: 4, fontSize: 11, fontWeight: 600, padding: '2px 7px' },
  label:      { color: '#374151', fontSize: 12, fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 4 },
  inp:        { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', marginTop: 2 },
  modal:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox:   { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
};
